import { observer } from "mobx-react";
import { PortfolioType, usePortfolio } from "../portfolioDomain";
import { useDispatch, useRootStore, useTranslation } from "../../stores/RootStoreContext";
import { useEffect, useState } from "react";
import { useAppBar } from "../../appBar/useAppBar";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { AppBar } from "../../components-v2/AppBar";
import { FollowUpLeftPanel } from "./followUpComponents";
import { isFailure, isOk, isPending } from "../../common/common";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { Box, Card, Container, VBox } from "../../components-v2/Box";
import { Icon } from "../../components-v2/Icon";
import { Button, LinkButton } from "../../components-v2/Button";
import { Tabs } from "../../components-v2/Tabs";
import { FollowUpCompanyInfoStore } from "./companyInfoFollowUpDomain";
import { navigate } from "../../events/eventsDomain";
import { adminArticles } from "../../utils/navigation";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect } from "../../components-v2/Select";

export const FollowUpCompanyInfoPage = observer(function PortfolioCompanyInfoPage({
  portfolioType,
  portfolioId,
  contextCompany,
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  contextCompany?: string;
}) {
  const { userStore, messageStore, http } = useRootStore();
  const { t } = useTranslation();
  const { portfolio } = usePortfolio({
    activeHeader: "follow-up",
    portfolioId,
    portfolioType,
  });

  const [store] = useState(new FollowUpCompanyInfoStore(http, userStore, messageStore));
  useEffect(() => {
    store.fetchCompanyInfo(portfolio, contextCompany);
  }, [store, portfolio, contextCompany]);

  const appBar = useAppBar({
    portfolioHeader: "follow-up",
    isPortfolioPage: contextCompany === undefined,
    selectedPortfolioOption: portfolio,
  });

  return (
    <FullScreenLayout
      alertStore={messageStore}
      appBar={<AppBar data={appBar} disableDropShadow />}
      centerPanelContainerProps={{ htmlProps: { style: { flex: "1 1 auto" } } }}
      leftPanelContainerProps={{ htmlProps: { style: { flex: "0 0 300px" } } }}
      leftPanel={
        <FollowUpLeftPanel
          portfolio={portfolio}
          selectedTab={"company-info"}
          showAdminLinks={true}
          signDocumentCount={
            isOk(store.companyInfoResponse)
              ? store.companyInfoResponse.data.signDocumentCount
              : undefined
          }
          followUpMattersCount={
            isOk(store.companyInfoResponse)
              ? store.companyInfoResponse.data.followUpMattersCount
              : undefined
          }
          companyInfoCount={
            isOk(store.companyInfoResponse)
              ? store.companyInfoResponse.data.companyInfoCount
              : undefined
          }
          followUpShareholdersCount={
            isOk(store.companyInfoResponse)
              ? store.companyInfoResponse.data.followUpShareholdersCount
              : undefined
          }
          contextCompany={contextCompany}
        />
      }
      centerPanel={
        isOk(store.companyInfoResponse) ? (
          <FollowUpCompanyInfoView store={store} />
        ) : isPending(store.companyInfoResponse) ? (
          <LoadingIndicator fullscreen />
        ) : isFailure(store.companyInfoResponse) ? (
          <VBox alignItems={"center"} justifyContent={"center"} fullheight>
            <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
            <Box>{t("error.fetch.data")}</Box>
            <LinkButton
              label={{ key: "try-again" }}
              onClick={() => store.fetchCompanyInfo(portfolio, contextCompany)}
            />
          </VBox>
        ) : undefined
      }
      disableAppBarShadow
    />
  );
});

const FollowUpCompanyInfoView = observer(function PortfolioCompanyInfoView({
  store,
}: {
  store: FollowUpCompanyInfoStore;
}) {
  const { t, lang } = useTranslation();
  const dispatch = useDispatch();
  const tabs = [
    {
      id: "follow-up",
      label: {
        key: "portfolio.company-info.for-followup" as const,
      },
      onSelect: () => {},
      isActive: true,
    },
  ];

  if (isOk(store.companyInfoResponse) && store.companyInfoResponse.data.companyInfoCount === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <Box bg>
        <VBox maxWidth="xl" container marginTop="lg">
          <h4>{t("portfolio.company-info")}</h4>
          <Tabs tabs={tabs} />
        </VBox>
      </Box>
      <Container padding maxWidth="xl">
        <Box>
          <Box>
            <MTextInput model={store.searchField} type={"search"} icon={"magnifying-glass"} />
            {store.companySelect && <MSelect model={store.companySelect} />}
          </Box>
        </Box>
        {store.filteredData(lang).map((followUp) => (
          <Card key={followUp.type + followUp.orgNo} padding={"lg"} gap={"md"}>
            <Box>
              <LinkButton
                weight={"normal"}
                label={{
                  key: "followup.company-info.aoa-date-link",
                  params: { companyName: followUp.companyName },
                }}
                onClick={() => dispatch([navigate(adminArticles(followUp.orgNo))])}
              />
            </Box>
            <h5>{t("followup.company-info.aoa-date-header")}</h5>
            {t("followup.company-info.aoa-date-text", {
              brAoADate: { type: "date", value: followUp.brAoADate },
              lastApprovedDate: { type: "date", value: followUp.lastApprovedDate },
            })}
            <Box>
              <Button
                variant={"secondary"}
                label={{ key: "followup.company-info.resolve" }}
                onClick={() => dispatch([navigate(adminArticles(followUp.orgNo))])}
              />
            </Box>
          </Card>
        ))}
      </Container>
    </>
  );
});

const EmptyState = observer(function EmptyState() {
  const { t } = useTranslation();
  return (
    <VBox fullheight alignItems={"center"} justifyContent={"center"}>
      <VBox alignItems={"center"} maxWidth={"md"}>
        <Icon name={"buildings"} size={"jumbo"} duotone />
        <h3>{t("followup.company-info.empty-state.header")}</h3>
        <Box textAlign={"center"}>{t("followup.company-info.empty-state.text")}</Box>
      </VBox>
    </VBox>
  );
});
